import type {
    ProfileTaskDraftReference,
    ProfileTaskParentContext,
    ProfileTaskSummary,
    ProfileTaskVersionInput,
} from '../model/profile-task';
import { validateProfileTask } from '../validation/profile-task-validation';

export type ProfileTaskEditorState = {
    readonly appliedForm?: ProfileTaskVersionInput;
    readonly context: ProfileTaskParentContext;
    readonly form: ProfileTaskVersionInput;
    readonly isDirty: boolean;
    readonly sourceTaskVersionId?: string;
    readonly taskId: string;
};

export type ProfileTaskParentDraftState = {
    readonly applied: readonly ProfileTaskDraftReference[];
    readonly baseline: readonly ProfileTaskDraftReference[];
    readonly context: ProfileTaskParentContext;
    readonly editors: ReadonlyMap<string, ProfileTaskEditorState>;
    readonly removedOpenTaskIds: ReadonlySet<string>;
};

export type OpenProfileTaskEditorInput = {
    readonly context: ProfileTaskParentContext;
    readonly form: ProfileTaskVersionInput;
    readonly sourceTaskVersionId?: string;
    readonly taskId: string;
};

export type ProfileTaskSavePreparation =
    | { readonly blockedTaskIds: readonly string[]; readonly ok: false }
    | {
          readonly ok: true;
          readonly ordered: readonly ProfileTaskDraftReference[];
          readonly removedTaskIds: readonly string[];
      };

export const getProfileTaskParentDraftKey = ({
    architectureId,
    parentId,
    parentType,
}: ProfileTaskParentContext): string => `${architectureId}:${parentType}:${parentId}`;

const formsEqual = (left: ProfileTaskVersionInput | undefined, right: ProfileTaskVersionInput): boolean =>
    left !== undefined && JSON.stringify(left) === JSON.stringify(right);

export const hasProfileTaskDraftChanges = (parent: ProfileTaskParentDraftState | undefined): boolean => {
    if (parent === undefined) {
        return false;
    }
    if ([...parent.editors.values()].some(({ isDirty }) => isDirty)) {
        return true;
    }
    return JSON.stringify(parent.applied) !== JSON.stringify(parent.baseline);
};

export const canReplaceProfileTaskEditor = (parent: ProfileTaskParentDraftState | undefined, taskId: string): boolean =>
    parent?.editors.get(taskId)?.isDirty !== true;

export const createParentDraftState = (
    context: ProfileTaskParentContext,
    baseline: readonly ProfileTaskDraftReference[],
): ProfileTaskParentDraftState => ({
    applied: [...baseline],
    baseline: [...baseline],
    context,
    editors: new Map(),
    removedOpenTaskIds: new Set(),
});

const withAppliedReference = (
    applied: readonly ProfileTaskDraftReference[],
    reference: ProfileTaskDraftReference,
): readonly ProfileTaskDraftReference[] => {
    const existingIndex = applied.findIndex((candidate) => candidate.taskId === reference.taskId);
    if (existingIndex === -1) {
        return [...applied, reference];
    }
    const next = [...applied];
    next[existingIndex] = reference;
    return next;
};

/**
 * Opens an editor for a task that does not exist in the parent yet. The editor starts pristine
 * (`isDirty: false`, no `appliedForm`) so closing an untouched new task tile discards it silently;
 * the first buffered edit turns it dirty because there is no applied form to compare against.
 */
export const addTaskEditor = (
    parent: ProfileTaskParentDraftState,
    { context, form, sourceTaskVersionId, taskId }: OpenProfileTaskEditorInput,
): ProfileTaskParentDraftState => ({
    ...parent,
    editors: new Map(parent.editors).set(taskId, {
        context,
        form,
        isDirty: false,
        sourceTaskVersionId,
        taskId,
    }),
});

export const openTaskEditor = (
    parent: ProfileTaskParentDraftState,
    { context, form, sourceTaskVersionId, taskId }: OpenProfileTaskEditorInput,
): ProfileTaskParentDraftState => ({
    ...parent,
    editors: new Map(parent.editors).set(taskId, {
        appliedForm: form,
        context,
        form,
        isDirty: false,
        sourceTaskVersionId:
            sourceTaskVersionId ?? parent.applied.find((reference) => reference.taskId === taskId)?.sourceTaskVersionId,
        taskId,
    }),
});

/**
 * Commits the buffered editor form into the parent draft. Rejected (returns `undefined`) when the
 * buffered form is invalid, so the tile keeps the editor open and dirty and the form keeps rendering
 * its inline validation messages.
 */
export const applyTaskEditor = (
    parent: ProfileTaskParentDraftState,
    taskId: string,
): ProfileTaskParentDraftState | undefined => {
    const editor = parent.editors.get(taskId);
    if (editor === undefined || !validateProfileTask(editor.form).ok) {
        return undefined;
    }
    const reference: ProfileTaskDraftReference = {
        draft: editor.form,
        sourceTaskVersionId: editor.sourceTaskVersionId,
        taskId,
    };
    const editors = new Map(parent.editors);
    editors.set(taskId, { ...editor, appliedForm: editor.form, isDirty: false });
    return { ...parent, applied: withAppliedReference(parent.applied, reference), editors };
};

/**
 * Buffers tile edits inside the editor only. The parent draft (`applied`) stays untouched until the
 * user applies the changes, so an abandoned editor never leaks into the parent save payload.
 */
export const updateTaskEditor = (
    parent: ProfileTaskParentDraftState,
    taskId: string,
    form: ProfileTaskVersionInput,
): ProfileTaskParentDraftState => {
    const editor = parent.editors.get(taskId);
    if (editor === undefined) {
        return parent;
    }
    const editors = new Map(parent.editors);
    editors.set(taskId, { ...editor, form, isDirty: !formsEqual(editor.appliedForm, form) });
    return { ...parent, editors };
};

export const discardTaskEditor = (parent: ProfileTaskParentDraftState, taskId: string): ProfileTaskParentDraftState => {
    const editor = parent.editors.get(taskId);
    if (editor === undefined) {
        return parent;
    }
    const editors = new Map(parent.editors);
    if (editor.appliedForm !== undefined) {
        editors.set(taskId, { ...editor, form: editor.appliedForm, isDirty: false });
        return { ...parent, editors };
    }

    // A task that was never applied has no place in the parent draft, so any reference for it is
    // dropped instead of lingering as a ghost row. The editor itself is only marked clean rather
    // than deleted: the tile is usually mid close animation and would otherwise flip to its
    // "draft unavailable" fallback while it animates out.
    const isBaselineTask = parent.baseline.some((reference) => reference.taskId === taskId);
    editors.set(taskId, { ...editor, isDirty: false });
    return {
        ...parent,
        applied: isBaselineTask ? parent.applied : parent.applied.filter((reference) => reference.taskId !== taskId),
        editors,
    };
};

export const discardParentDraft = (parent: ProfileTaskParentDraftState): ProfileTaskParentDraftState => {
    const baselineIds = new Set(parent.baseline.map(({ taskId }) => taskId));
    const addedTaskIds = parent.applied.map(({ taskId }) => taskId).filter((taskId) => !baselineIds.has(taskId));
    return {
        ...parent,
        applied: parent.baseline,
        editors: new Map(),
        removedOpenTaskIds: new Set(addedTaskIds),
    };
};

export const removeTaskFromParent = (
    parent: ProfileTaskParentDraftState,
    taskId: string,
): ProfileTaskParentDraftState => {
    const editors = new Map(parent.editors);
    editors.delete(taskId);
    return {
        ...parent,
        applied: parent.applied.filter((reference) => reference.taskId !== taskId),
        editors,
        removedOpenTaskIds: new Set([...parent.removedOpenTaskIds, taskId]),
    };
};

/**
 * Removals are committed one by one before the parent PATCH, so when a later step of the save
 * fails, the tasks whose DELETE already went through must leave the draft's baseline: keeping
 * them would make the next save attempt repeat DELETE calls for tasks that no longer exist.
 */
export const adoptCompletedRemovals = (
    parent: ProfileTaskParentDraftState,
    removedTaskIds: readonly string[],
): ProfileTaskParentDraftState => {
    if (removedTaskIds.length === 0) {
        return parent;
    }
    const removed = new Set(removedTaskIds);
    return {
        ...parent,
        applied: parent.applied.filter(({ taskId }) => !removed.has(taskId)),
        baseline: parent.baseline.filter(({ taskId }) => !removed.has(taskId)),
    };
};

export const reorderAppliedTasks = (
    parent: ProfileTaskParentDraftState,
    fromIndex: number,
    toIndex: number,
): ProfileTaskParentDraftState => {
    if (
        fromIndex === toIndex ||
        fromIndex < 0 ||
        toIndex < 0 ||
        fromIndex >= parent.applied.length ||
        toIndex >= parent.applied.length
    ) {
        return parent;
    }
    const applied = [...parent.applied];
    const [moved] = applied.splice(fromIndex, 1);
    if (moved !== undefined) {
        applied.splice(toIndex, 0, moved);
    }
    return { ...parent, applied };
};

export const prepareParentSave = (parent: ProfileTaskParentDraftState): ProfileTaskSavePreparation => {
    const validated = parent.applied.map((reference) => ({
        reference,
        validation: reference.draft === undefined ? undefined : validateProfileTask(reference.draft),
    }));
    const blockedTaskIds = validated
        .filter(({ validation }) => validation !== undefined && !validation.ok)
        .map(({ reference }) => reference.taskId);
    if (blockedTaskIds.length > 0) {
        return { blockedTaskIds, ok: false };
    }
    const appliedTaskIds = new Set(parent.applied.map(({ taskId }) => taskId));
    return {
        ok: true,
        ordered: validated.map(({ reference, validation }) =>
            validation?.ok === true ? { ...reference, draft: validation.value } : reference,
        ),
        removedTaskIds: parent.baseline.map(({ taskId }) => taskId).filter((taskId) => !appliedTaskIds.has(taskId)),
    };
};

/**
 * Adopts a task that was just persisted through the generate endpoint into both the baseline and
 * the applied list: it exists on the server regardless of whether the parent edit session is later
 * saved or discarded.
 */
export const appendPersistedTaskReference = (
    parent: ProfileTaskParentDraftState,
    task: { readonly taskId: string; readonly taskVersionId: string },
): ProfileTaskParentDraftState => {
    if (parent.baseline.some(({ taskId }) => taskId === task.taskId)) {
        return parent;
    }
    const reference: ProfileTaskDraftReference = { sourceTaskVersionId: task.taskVersionId, taskId: task.taskId };
    return {
        ...parent,
        applied: parent.applied.some(({ taskId }) => taskId === task.taskId)
            ? parent.applied
            : [...parent.applied, reference],
        baseline: [...parent.baseline, reference],
    };
};

export const adoptSavedBaseline = (
    parent: ProfileTaskParentDraftState,
    context: ProfileTaskParentContext,
    parentVersion: number,
    summaries: readonly ProfileTaskSummary[],
): ProfileTaskParentDraftState => {
    const baseline = summaries.map(({ taskId, taskVersionId }) => ({
        sourceTaskVersionId: taskVersionId,
        taskId,
    }));
    return {
        ...parent,
        applied: baseline,
        baseline,
        context: { ...context, parentVersion },
        editors: new Map(),
        removedOpenTaskIds: new Set(),
    };
};
